import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, BookOpenIcon, ClockIcon, SearchIcon } from 'lucide-react';
import ArticleCard from '../../components/Articles/ArticleCard';
import { TroveLayout } from '../../components/Trove/TroveLayout';
import { FilterSection } from '../../components/UI/FilterSidebar';
import { SearchWithAutocomplete } from '../../components/UI/SearchWithAutocomplete';
import { useDarkMode } from '../../context/DarkModeContext';
import { useUserRole } from '../../context/UserRoleContext';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { useDarkClasses } from '../../hooks/useDarkClasses';
interface Article {
  id: number;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  readTime: string;
  image?: string;
  featured?: boolean;
}
const allArticles: Article[] = [{
  id: 1,
  title: 'Character Relationships Are Representation, Not Trigger Warnings',
  excerpt: 'A response to recent discussions about the labeling of LGBTQ+ and non-traditional relationships in romance literature, and why framing them as warnings does more harm than good.',
  author: 'Dr. Sarah Chen',
  date: 'October 15, 2023',
  category: 'Industry Insights',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1467991521834-fb8e202c7074?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  featured: true
}, {
  id: 2,
  title: 'The Evolution of Romance Tropes in 2023',
  excerpt: 'How classic romance tropes are being reimagined for modern readers — from enemies-to-lovers to found family dynamics that feel fresh and earned.',
  author: 'Emma Clarke',
  date: 'October 9, 2023',
  category: 'Romance',
  readTime: '7 min read',
  image: 'https://images.unsplash.com/photo-1474552226712-ac0f0961a954?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 3,
  title: 'Diversity in Romance: Beyond the Mainstream',
  excerpt: 'Exploring the growing diversity in romance fiction and its impact on readers who finally see themselves in the stories they love.',
  author: 'Priya Sharma',
  date: 'October 6, 2023',
  category: 'Romance',
  readTime: '9 min read',
  image: 'https://images.unsplash.com/photo-1516575334481-f85287c2c82d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 4,
  title: 'Voice Acting Essentials for New Narrators',
  excerpt: 'A comprehensive guide to getting started in audiobook narration — from microphone technique to character differentiation and pacing.',
  author: 'Sarah Johnson',
  date: 'October 7, 2023',
  category: 'Audio & Narration',
  readTime: '12 min read',
  image: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 5,
  title: 'Home Studio Setup for Audiobook Narrators',
  excerpt: 'Everything you need to create a professional home recording studio on a budget — acoustic treatment, microphones, and DAW recommendations.',
  author: 'Mike Rodriguez',
  date: 'October 4, 2023',
  category: 'Audio & Narration',
  readTime: '15 min read',
  image: 'https://images.unsplash.com/photo-1598653222000-6b7b7a552625?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 6,
  title: 'How to Price Your Indie Book in 2023',
  excerpt: 'A data-driven look at ebook, print, and audiobook pricing strategies across genres — what readers expect and what actually converts.',
  author: 'James Whitfield',
  date: 'October 3, 2023',
  category: 'Author Resources',
  readTime: '10 min read',
  image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 7,
  title: 'Building a Reader Newsletter That Actually Grows',
  excerpt: "Email remains the most reliable channel for indie authors. Here's how to build a list, write compelling content, and convert subscribers into buyers.",
  author: 'Lena Park',
  date: 'September 30, 2023',
  category: 'Author Resources',
  readTime: '11 min read',
  image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 8,
  title: 'The Rise of Dark Fantasy in Indie Publishing',
  excerpt: "Dark fantasy has exploded in indie markets. We look at what's driving the trend, which subgenres are growing fastest, and what readers want next.",
  author: 'Marcus Webb',
  date: 'September 28, 2023',
  category: 'Fantasy',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 9,
  title: 'Cozy Mystery: The Genre That Keeps Growing',
  excerpt: "From small-town sleuths to culinary detectives, cozy mystery continues to outperform expectations. An inside look at the genre's loyal readership.",
  author: 'Claire MacAllister',
  date: 'September 25, 2023',
  category: 'Mystery & Thriller',
  readTime: '6 min read',
  image: 'https://images.unsplash.com/photo-1551269901-5c5e14c25df7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 10,
  title: 'Cover Design Trends Dominating Indie Romance',
  excerpt: "Illustrated covers, bold typography, and illustrated character art are reshaping romance shelves. A visual breakdown of what's working in 2023.",
  author: 'Luna Design Co.',
  date: 'September 22, 2023',
  category: 'Industry Insights',
  readTime: '7 min read',
  image: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 11,
  title: "Science Fiction's New Wave: Climate and Identity",
  excerpt: 'Contemporary indie sci-fi is grappling with climate futures and identity politics in ways that feel urgent and deeply human. A genre survey.',
  author: 'Dr. Aiden Park',
  date: 'September 19, 2023',
  category: 'Fantasy',
  readTime: '9 min read',
  image: 'https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 12,
  title: 'What Readers Actually Want From Book Descriptions',
  excerpt: 'We analyzed 500 bestselling indie book descriptions to find the patterns that drive clicks and conversions. The results might surprise you.',
  author: 'Zoe Chen',
  date: 'September 16, 2023',
  category: 'Author Resources',
  readTime: '13 min read',
  image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}];
const CATEGORIES = ['All', 'Industry Insights', 'Romance', 'Fantasy', 'Mystery & Thriller', 'Audio & Narration', 'Author Resources'];
const READ_TIMES = ['All', 'Under 8 min', '8–12 min', '12+ min'];
const parseMinutes = (readTime: string): number => {
  const match = readTime.match(/(\d+)/);
  return match ? parseInt(match[1]) : 0;
};
const AllArticlesPage = () => {
  const {
    darkMode
  } = useDarkMode();
  const {
    isReader
  } = useUserRole();
  const rt = useRoleTheme();
  const dk = useDarkClasses();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeReadTime, setActiveReadTime] = useState('All');
  const featured = allArticles.find(a => a.featured)!;
  const rest = allArticles.filter(a => !a.featured);
  const filtered = useMemo(() => {
    return rest.filter(article => {
      const matchesSearch = !searchQuery || article.title.toLowerCase().includes(searchQuery.toLowerCase()) || article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) || article.author.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'All' || article.category === activeCategory;
      const mins = parseMinutes(article.readTime);
      const matchesReadTime = activeReadTime === 'All' || activeReadTime === 'Under 8 min' && mins < 8 || activeReadTime === '8–12 min' && mins >= 8 && mins <= 12 || activeReadTime === '12+ min' && mins > 12;
      return matchesSearch && matchesCategory && matchesReadTime;
    });
  }, [searchQuery, activeCategory, activeReadTime, rest]);
  const filterSections: FilterSection[] = [{
    id: 'category',
    title: 'Category',
    type: 'radio',
    value: activeCategory,
    onChange: val => setActiveCategory(val),
    options: CATEGORIES.map(c => ({
      value: c,
      label: c
    }))
  }, {
    id: 'readtime',
    title: 'Read Time',
    type: 'radio',
    value: activeReadTime,
    onChange: val => setActiveReadTime(val),
    options: READ_TIMES.map(r => ({
      value: r,
      label: r
    }))
  }];
  const handleReset = () => {
    setActiveCategory('All');
    setActiveReadTime('All');
    setSearchQuery('');
  };
  return <TroveLayout filterSections={filterSections} onResetFilters={handleReset} data-id="element-15793" data-mp="2wznhpd3nnvrt">
      {/* Header */}
      <div className="mb-8" data-id="element-15794" data-mp="3aupwyque6xha">
        <div className="flex items-center gap-2 mb-2" data-id="element-15795" data-mp="35xhgcuow71i5">
          <BookOpenIcon className={`h-5 w-5 ${rt.textPrimary}`} data-id="element-15796" data-mp="35vwh4rrmuudy" />
          <Link to="/trove" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-15797" data-mp="2i97xa0j2crup">
            The Trove
          </Link>
          <ChevronRightIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-15798" data-mp="2e8o04wyhpo8k" />
          <span className={`text-sm ${dk.textMuted}`} data-id="element-15799" data-mp="2rz9lqtfbwca5">All Articles</span>
        </div>
        <h1 className={`text-3xl sm:text-4xl font-bold mb-3 ${dk.textPrimary}`} data-id="element-15800" data-mp="2n6zn8p05e3w9">
          All Articles
        </h1>
        <p className={`text-base max-w-2xl mb-6 ${dk.textMuted}`} data-id="element-15801" data-mp="1zgvlkn6ise7w">
          {isReader ? 'In-depth reads on books, genres, authors, and the indie publishing world.' : 'Industry analysis, author resources, and publishing insights for indie creators.'}
        </p>
        <div className="max-w-md" data-id="element-15802" data-mp="c6m0xm7s3gew">
          <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} placeholder="Search articles, authors, topics..." variant="pill" suggestions={[]} data-id="element-15803" data-mp="c7ortu53piut" />
        </div>
      </div>

      <div className="space-y-10" data-id="element-15804" data-mp="3ucs0tpy6uf0i">
        {/* Featured Article */}
        {!searchQuery && activeCategory === 'All' && activeReadTime === 'All' && <section data-id="element-15805" data-mp="3kmp013ktrwrm">
              <h2 className={`text-lg font-semibold mb-4 ${dk.textPrimary}`} data-id="element-15806" data-mp="3klxe6kh15dej">
                Featured
              </h2>
              <article className={`${dk.cardSubtle} rounded-xl shadow-sm border overflow-hidden`} data-id="element-15807" data-mp="3fox5s2wpjyk9">
                <div className="md:flex" data-id="element-15808" data-mp="3bo3h37bfyfqb">
                  <div className="md:w-2/5" data-id="element-15809" data-mp="36mnxljowwha1">
                    <img src={featured.image} alt={featured.title} className="w-full h-52 md:h-full object-cover" data-id="element-15810" data-mp="36npj6i3k0m60" />
                  </div>
                  <div className="md:w-3/5 p-6 flex flex-col justify-between" data-id="element-15811" data-mp="1v5jyub96sm6v">
                    <div data-id="element-15812" data-mp="1v3gxiv2l36mu">
                      <div className="flex items-center gap-2 mb-3" data-id="element-15813" data-mp="204egh9vf44pe">
                        <span className={`px-2.5 py-0.5 text-white text-xs font-medium rounded-full ${rt.badgeBg}`} data-id="element-15814" data-mp="202k4il24jmxg">
                          {featured.category}
                        </span>
                        <span className={`text-xs ${dk.textMuted}`} data-id="element-15815" data-mp="1cek7j3e7z0af">
                          {featured.date}
                        </span>
                      </div>
                      <h3 className={`text-xl font-bold mb-2 leading-snug ${dk.textPrimary}`} data-id="element-15816" data-mp="1h7mxl1jsfrlv">
                        {featured.title}
                      </h3>
                      <p className={`text-sm mb-4 line-clamp-3 ${dk.textSecondary}`} data-id="element-15817" data-mp="1s05a03smaw4e">
                        {featured.excerpt}
                      </p>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-15818" data-mp="20syv4huqwpq6">
                      <div className="text-xs" data-id="element-15819" data-mp="1wrdkl35oonj5">
                        <p className={`font-semibold ${dk.textHeading}`} data-id="element-15820" data-mp="1wrnc4v6dn2qy">
                          {featured.author}
                        </p>
                        <p className={`flex items-center gap-1 mt-0.5 ${dk.textMuted}`} data-id="element-15821" data-mp="142g517sux4u3">
                          <ClockIcon className="h-3 w-3" data-id="element-15822" data-mp="1i30lhxbauokf" />
                          {featured.readTime}
                        </p>
                      </div>
                      <button className={`text-sm font-medium ${rt.accentLink}`} data-id="element-15823" data-mp="2th4dwz28o4nd">
                        Read full article →
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            </section>}

        {/* Category quick-filters (inline chips) */}
        <div className="flex flex-wrap gap-2" data-id="element-15824" data-mp="30ykfvasb5f3d">
          {CATEGORIES.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${activeCategory === cat ? isReader ? darkMode ? 'bg-indigo-900/40 text-indigo-300 ring-1 ring-indigo-700' : 'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200' : darkMode ? 'bg-purple-900/40 text-purple-300 ring-1 ring-purple-700' : 'bg-purple-100 text-purple-700 ring-1 ring-purple-200' : darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 border border-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`} data-id="element-15825" data-mp="2n38lj4i2dqmz">
              {cat}
            </button>)}
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between" data-id="element-15826" data-mp="2eu2tfvzjye0v">
          <p className={`text-sm ${dk.textMuted}`} data-id="element-15827" data-mp="2erglvwcnvyin">
            {filtered.length} article{filtered.length !== 1 ? 's' : ''}
            {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
            {searchQuery ? ` matching "${searchQuery}"` : ''}
          </p>
          {(activeCategory !== 'All' || activeReadTime !== 'All' || searchQuery) && <button onClick={handleReset} className={`text-xs font-medium ${rt.accentLink}`} data-id="element-15828" data-mp="2yh0ogp25oai8">
              Clear filters
            </button>}
        </div>

        {/* Articles Grid */}
        {filtered.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-15829" data-mp="2jrmizs1pad84">
            {filtered.map(article => <ArticleCard key={article.id} article={article} data-id="element-15830" data-mp="3h9crzf4jphty" />)}
          </div> : <div className={`text-center py-16 rounded-xl border border-dashed ${darkMode ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`} data-id="element-15831" data-mp="3cg0dhxtkyrex">
            <BookOpenIcon className={`h-8 w-8 mx-auto mb-3 opacity-30 ${dk.textMuted}`} data-id="element-15832" data-mp="3os2uqnsuqeka" />
            <p className={`text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-15833" data-mp="3tpcjs2cbwo7l">
              No articles found
            </p>
            <p className={`text-xs mb-4 ${dk.textMuted}`} data-id="element-15834" data-mp="6lxxtmxnh1fg">
              Try adjusting your filters or search term
            </p>
            <button onClick={handleReset} className={`text-sm font-medium ${rt.accentLink}`} data-id="element-15835" data-mp="bk98fzvrrfyq">
              Clear all filters
            </button>
          </div>}
      </div>
    </TroveLayout>;
};
export default AllArticlesPage;